import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dropdown, Empty, Form, Input, message, Modal, Tooltip, Tree } from 'antd'
import type { DataNode } from 'antd/es/tree'
import {
  SearchOutlined,
  FolderOutlined,
  FileTextOutlined,
  MinusSquareOutlined,
  MoreOutlined,
  PlusSquareOutlined,
} from '@ant-design/icons'
import useRequest from 'components/common/hooks/useRequest'
import { getRet00cdTree, updateRet00cd } from 'components/common/services/ret00cds'
import { getRet00cOverviewTree } from 'components/common/services/ret00cs'

interface Ret00cSidebarProps {
  selectedGroupId?: number
  selectedRet00cId?: number
  onSelectGroup?: (groupId?: number) => void
  overviewRefreshDeps?: unknown[]
  groupRefreshDeps?: unknown[]
}

const HTTP_COLOR: Record<string, string> = {
  GET: '#52c41a',
  POST: '#1890ff',
  PUT: '#faad14',
  DELETE: '#ff4d4f',
  PATCH: '#722ed1',
}

const GOLD = '#D4B86A'

export function useRet00cSidebarTree(
  refreshDeps: unknown[] = [],
): {
  rawTree: any[]
  groupTreeOptions: any[]
  loading: boolean
  reloadOverviewTree: () => Promise<any>
  reloadGroupTree: () => Promise<any>
} {
  const {
    data: overviewData,
    loading,
    run: reloadOverviewTree,
  } = useRequest(() => getRet00cOverviewTree(), { refreshDeps })
  const { data: groupTreeData, run: reloadGroupTree } = useRequest(() => getRet00cdTree(), {
    refreshDeps,
  })
  const rawTree = useMemo(() => ((overviewData as any)?.data?.items || []), [overviewData])
  const groupTreeOptions = useMemo(() => ((groupTreeData as any)?.data?.items || []), [groupTreeData])
  return { rawTree, groupTreeOptions, loading, reloadOverviewTree, reloadGroupTree }
}

function Ret00cSidebar({
  selectedGroupId,
  selectedRet00cId,
  onSelectGroup,
  overviewRefreshDeps,
  groupRefreshDeps,
}: Ret00cSidebarProps): JSX.Element {
  const navigate = useNavigate()
  const [keyword, setKeyword] = useState('')
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])
  const [editingGroup, setEditingGroup] = useState<{ id: number; name: string } | null>(null)
  const [editForm] = Form.useForm<{ name: string }>()
  const { data: overviewData, loading, run: reloadOverviewTree } = useRequest(() => getRet00cOverviewTree(), {
    refreshDeps: overviewRefreshDeps,
  })
  const { run: reloadGroupTree } = useRequest(() => getRet00cdTree(), {
    refreshDeps: groupRefreshDeps,
  })
  void reloadGroupTree
  const rawTree = useMemo(() => ((overviewData as any)?.data?.items || []), [overviewData])

  const handleSelectGroup = (groupId?: number) => {
    if (!groupId) {
      return
    }

    if (onSelectGroup) {
      onSelectGroup(groupId)
      return
    }

    navigate(`/ret00cs?ret00cd_id=${groupId}`)
  }

  const handleNodeTitleClick = (node: any, event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (node.node_type === 'interface') {
      navigate(`/ret00cs/${node.id}`)
      return
    }

    handleSelectGroup(node.id as number | undefined)
  }

  const handleEditGroup = (node: any) => {
    const name = typeof node.title === 'string' ? node.title : node.title?.props?.title ?? node.name ?? ''
    setEditingGroup({ id: node.id, name })
    editForm.setFieldsValue({ name })
  }

  const handleDeleteGroup = () => {
    message.info('删除功能暂未开放')
  }

  const handleEditGroupSubmit = async () => {
    if (!editingGroup) return
    try {
      const values = await editForm.validateFields()
      await updateRet00cd(editingGroup.id, { name: values.name })
      message.success('分组名称修改成功')
      setEditingGroup(null)
      await reloadOverviewTree()
    } catch (error: any) {
      const firstError = error?.errorFields?.find((field: any) => field?.errors?.length)
      message.error(error?.response?.data?.message || firstError?.errors?.[0] || '分组名称修改失败')
    }
  }
  void reloadOverviewTree

  const matchTree = (nodes: any[]): boolean => {
    const k = keyword.trim().toLowerCase()
    if (!k) return true
    const walk = (list: any[]): boolean =>
      list.some((n) => {
        const titleText =
          typeof n.title === 'string' ? n.title : n.title?.props?.title ?? n.title?.toString() ?? ''
        const hit =
          titleText.toLowerCase().includes(k) ||
          (n.path || '').toLowerCase().includes(k) ||
          (n.code || '').toLowerCase().includes(k)
        return hit || (n.children && n.children.length > 0 && walk(n.children))
      })
    return walk(nodes)
  }

  const filteredRaw = useMemo(() => {
    const k = keyword.trim().toLowerCase()
    if (!k) return rawTree
    const filter = (list: any[]): any[] =>
      list
        .map((n) => {
          const children = n.children && n.children.length > 0 ? filter(n.children) : []
          const titleText =
            typeof n.title === 'string' ? n.title : n.title?.props?.title ?? n.title?.toString() ?? ''
          const hit =
            titleText.toLowerCase().includes(k) ||
            (n.path || '').toLowerCase().includes(k) ||
            (n.code || '').toLowerCase().includes(k)
          if (hit || children.length > 0) {
            return { ...n, children }
          }
          return null
        })
        .filter(Boolean) as any[]
    return filter(rawTree)
  }, [rawTree, keyword])

  const renderTitle = (node: any): React.ReactNode => {
    const isGroup = node.node_type === 'group'
    const rawTitle: any = node.title
    const titleText =
      typeof rawTitle === 'string' ? rawTitle : rawTitle?.props?.title ?? rawTitle?.toString() ?? ''

    if (isGroup) {
      return (
        <Tooltip title={titleText} placement="right">
          <span
            onClick={(event) => handleNodeTitleClick(node, event)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              maxWidth: '100%',
              color: GOLD,
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            <span style={{ color: GOLD, display: 'inline-flex', alignItems: 'center' }}>
              <FolderOutlined />
            </span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {titleText}
            </span>
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  { key: 'edit', label: '编辑' },
                  { key: 'delete', label: '删除' },
                ],
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation()
                  if (key === 'edit') {
                    handleEditGroup(node)
                  } else if (key === 'delete') {
                    handleDeleteGroup()
                  }
                },
              }}
            >
              <span
                className="ret00c-group-more"
                onClick={(event) => event.stopPropagation()}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  marginLeft: 2,
                  flexShrink: 0,
                  color: '#595959',
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
              >
                <MoreOutlined style={{ fontSize: 14 }} />
              </span>
            </Dropdown>
          </span>
        </Tooltip>
      )
    }

    const http = (node.http_method || 'GET').toUpperCase()
    return (
      <Tooltip title={titleText} placement="right">
        <span
          onClick={(event) => handleNodeTitleClick(node, event)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            maxWidth: '100%',
            color: '#262626',
            cursor: 'pointer',
          }}
        >
          <FileTextOutlined style={{ color: '#595959', fontSize: 14 }} />
          <span
            style={{
              color: HTTP_COLOR[http] || '#333',
              fontSize: 11,
              fontWeight: 700,
              minWidth: 30,
              width: 30,
              flexShrink: 0,
            }}
          >
            {http}
          </span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {titleText}
          </span>
        </span>
      </Tooltip>
    )
  }

  const decorateTreeNodes = (nodes: any[]): DataNode[] =>
    nodes.map((node) => {
      const children = node.children && node.children.length > 0 ? decorateTreeNodes(node.children) : undefined
      return {
        key: node.key,
        title: renderTitle(node),
        isLeaf: node.isLeaf === true || (node.node_type !== 'group' && !children),
        children,
        dataRef: node,
        icon: undefined,
      } as DataNode
    })

  const decoratedTreeData = useMemo(() => decorateTreeNodes(filteredRaw), [filteredRaw])

  const hasData = matchTree(rawTree)

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 12,
        boxSizing: 'border-box',
        minWidth: 0,
      }}
    >
      <div
        style={{
          background: GOLD,
          borderRadius: 0,
          padding: 12,
          marginBottom: 12,
        }}
      >
        <Input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索分组 / 接口"
          allowClear
          size="middle"
          prefix={<SearchOutlined style={{ color: '#8c8c8c' }} />}
          style={{
            borderRadius: 0,
            background: '#fff',
          }}
        />
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          background: '#fff',
          borderRadius: 0,
          padding: 12,
          border: '1px solid #f5efe0',
        }}
      >
        {loading && !overviewData ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#8c8c8c' }}>加载中...</div>
        ) : !hasData ? (
          <Empty description="暂无匹配的接口" />
        ) : decoratedTreeData.length > 0 ? (
          <Tree
            blockNode
            expandAction={false}
            expandedKeys={expandedKeys}
            onExpand={(keys) => setExpandedKeys(keys)}
            icon={undefined}
            selectedKeys={
              selectedRet00cId
                ? [`ret00c-${selectedRet00cId}`]
                : selectedGroupId
                  ? [`ret00cd-${selectedGroupId}`]
                  : []
            }
            switcherIcon={(props) => {
              if (props.isLeaf) return <span style={{ width: 16, display: 'inline-block' }} />
              return props.expanded ? (
                <MinusSquareOutlined style={{ color: '#595959', fontSize: 16 }} />
              ) : (
                <PlusSquareOutlined style={{ color: '#595959', fontSize: 16 }} />
              )
            }}
            treeData={decoratedTreeData}
            onSelect={(_, info) => {
              const node = info.node as any
              const ref: any = node.dataRef
              if (!ref) return
              if (ref.node_type === 'interface') {
                navigate(`/ret00cs/${ref.id}`)
                return
              }
              handleSelectGroup(ref.id as number | undefined)
            }}
            style={{
              fontSize: 14,
              color: '#262626',
            }}
            showLine={{ showLeafIcon: false }}
            className="ret00c-sidebar-tree"
          />
        ) : (
          <Empty description="暂无接口分组" />
        )}
      </div>

      <Modal
        open={!!editingGroup}
        title="编辑分组"
        onCancel={() => setEditingGroup(null)}
        onOk={handleEditGroupSubmit}
        destroyOnClose
        getContainer={() => document.body}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="分组名称"
            rules={[{ required: true, message: '请输入分组名称' }]}
          >
            <Input placeholder="请输入分组名称" />
          </Form.Item>
        </Form>
      </Modal>

      <style>{`
        .ret00c-sidebar-tree {
          --ant-tree-node-content-height: 32px;
          font-size: 14px;
        }
        .ret00c-sidebar-tree .ant-tree-node-content-wrapper {
          border-radius: 6px;
        }
        .ret00c-sidebar-tree .ant-tree-node-content-wrapper:hover {
          background: #faf6ec;
        }
        .ret00c-sidebar-tree .ant-tree-node-selected > .ant-tree-node-content-wrapper,
        .ret00c-sidebar-tree .ant-tree-node-selected > .ant-tree-node-content-wrapper:hover {
          background: #faf0d7 !important;
        }
        .ret00c-sidebar-tree li {
          padding: 2px 0 !important;
        }
        .ret00c-sidebar-tree .ant-tree-switcher {
          width: 20px;
        }
        .ret00c-sidebar-tree .ant-tree-title {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ret00c-sidebar-tree .ant-tree-show-line li span.ant-tree-switcher-line-icon {
          display: none;
        }
        .ret00c-group-more {
          opacity: 0;
          transition: opacity 0.15s;
        }
        .ret00c-sidebar-tree .ant-tree-node-content-wrapper:hover .ret00c-group-more {
          opacity: 1;
        }
      `}</style>
    </div>
  )
}

export default Ret00cSidebar
